import React, { useState, useRef, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Send, MessageCircle, Database, BookOpen, Users, FileText } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useSEO from "../hooks/useSEO";

const Demo = () => {
  // SEO para la página de demo
  useSEO({
    title: "Demo de IA - Innova2Market | Prueba nuestro asistente inteligente",
    description: "Prueba nuestro demo de IA para clínicas estéticas. Consulta base de datos SQL y conocimiento especializado en tratamientos estéticos.",
    keywords: "demo IA, chatbot estética, consulta base datos, tratamientos estéticos, automatización clínicas",
    ogTitle: "Demo de IA - Innova2Market",
    ogDescription: "Prueba nuestro demo de IA para clínicas estéticas. Consulta base de datos SQL y conocimiento especializado."
  });

  // Generar o recuperar nonce persistente
  const [nonce] = useState(() => {
    const storedNonce = localStorage.getItem('demo-nonce');
    if (storedNonce) {
      return storedNonce;
    }
    // Generar nuevo nonce si no existe
    const newNonce = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('demo-nonce', newNonce);
    return newNonce;
  });

  const [messages, setMessages] = useState(() => {
    // Recuperar mensajes guardados o usar mensaje inicial
    const storedMessages = localStorage.getItem('demo-messages');
    if (storedMessages) {
      try {
        const parsedMessages = JSON.parse(storedMessages);
        // Convertir las fechas de string a Date objects
        return parsedMessages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } catch (error) {
        console.error('Error parsing stored messages:', error);
      }
    }
    
    // Mensaje inicial si no hay mensajes guardados
    return [{
      id: 1,
      type: "bot",
      content: "¡Hola! Soy tu asistente virtual para clínicas estéticas. Puedo ayudarte con consultas sobre facturas, productos, contactos y tratamientos. ¿En qué puedo ayudarte hoy?",
      timestamp: new Date()
    }];
  });
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const scrollToBottom = () => {
    // Solo hacer scroll dentro del contenedor del chat, no en toda la página
    const chatContainer = document.querySelector('.chat-messages-container');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  };

  // Función para guardar mensajes en localStorage
  const saveMessagesToStorage = (messagesToSave) => {
    try {
      localStorage.setItem('demo-messages', JSON.stringify(messagesToSave));
    } catch (error) {
      console.error('Error saving messages to localStorage:', error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Guardar mensajes en localStorage cada vez que cambien
  useEffect(() => {
    saveMessagesToStorage(messages);
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    // Enviar mensaje al webhook
    try {
      const response = await fetch("https://demo-agl-n8n.56acma.easypanel.host/webhook/a4279239-2c9b-489b-afa9-2461e6a8d6f1", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: inputMessage, nonce: nonce }),
      });

      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          id: Date.now() + 1,
          type: "bot",
          content: data.output || "No se recibió respuesta del servidor.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: "bot",
          content: `Error del servidor: ${response.status} ${response.statusText}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
      setIsLoading(false);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: "Error de conexión. No se pudo conectar con el servidor.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearConversation = () => {
    const initialMessage = {
      id: Date.now(),
      type: "bot",
      content: "¡Hola! Soy tu asistente virtual para clínicas estéticas. Puedo ayudarte con consultas sobre facturas, productos, contactos y tratamientos. ¿En qué puedo ayudarte hoy?",
      timestamp: new Date()
    };
    setMessages([initialMessage]);
    localStorage.removeItem('demo-messages');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Panel de funcionalidades */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center">
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-3 text-purple-600" />
                Funcionalidades del Chat
              </h2>
              
              <div className="space-y-6">
                {/* Base de datos SQL */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Database className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-800">Base de Datos SQL</h3>
                  </div>
                  <div className="pl-8 space-y-2">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-600">Facturas y pagos</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-600">Catálogo de productos</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-600">Base de contactos</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 pl-8">
                    Consulta información en tiempo real sobre facturas, productos y datos de contacto de clientes.
                  </p>
                </div>

                {/* Base de conocimiento */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-800">Base de Conocimiento</h3>
                  </div>
                  <div className="pl-8 space-y-2">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-600">Tratamientos estéticos</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-600">Protocolos médicos</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-gray-600">Información de productos</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 pl-8">
                    Accede a información detallada sobre tratamientos, protocolos y productos para resolver dudas de clientes.
                  </p>
                </div>

                {/* Ejemplos de consultas */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-800">Ejemplos de consultas:</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>• "¿Cuántas facturas pendientes tiene Nuria Castro Vega?"</p>
                    <p>• "¿Cuáles son los tratamientos esteticos más comunes?"</p>
                    <p>• "¿Cuál es el precio para un informe médico para el seguro?"</p>
                    <p>• "¿Cuáles son los 5 productos más vendidos de 2025?"</p>
                    <p>• "¿Cuántas facturas pendientes de pago hay?"</p>
                  </div>
                </div>
              </div>
            </div>

                         
          </div>

          {/* Chat */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg h-[500px] sm:h-[600px] flex flex-col">
              {/* Header del chat */}
              <div className="p-3 sm:p-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">Chat Demo - Clínica Estética</h3>
                    <p className="text-xs sm:text-sm text-gray-500">Asistente virtual con acceso a base de datos y conocimiento</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearConversation}
                    className="text-gray-600 hover:text-red-600 border-gray-300 hover:border-red-300 text-xs sm:text-sm"
                  >
                    Limpiar Chat
                  </Button>
                </div>
              </div>

              {/* Mensajes */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 chat-messages-container">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-xs sm:max-w-sm lg:max-w-md px-3 sm:px-4 py-2 rounded-lg ${
                        message.type === "user"
                          ? "bg-purple-600 text-white"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      <p className="text-xs sm:text-sm">{message.content}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {message.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 text-gray-800 px-3 sm:px-4 py-2 rounded-lg">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input del chat */}
              <div className="p-3 sm:p-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Escribe tu consulta aquí..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                    disabled={isLoading}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-2 rounded-lg"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Presiona Enter para enviar • Esta es una demostración del chat
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sección de funcionalidades adicionales */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-purple-800 py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6">
              ¡Y se pueden hacer
              <span className="block bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
                más cosas!
              </span>
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto px-4">
              Nuestro sistema no solo responde en el chat, también puede enviar notificaciones 
              y mensajes a múltiples plataformas automáticamente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* WhatsApp */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 group">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-green-400 transition-colors">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">WhatsApp</h3>
              <p className="text-white/80 text-xs sm:text-sm">
                Pide información o ejecuta tareas mandando un simple mensaje o audio por WhatsApp. Manda información u ofertas personalizadas a tus clientes por WhatsApp...
              </p>
            </div>

            {/* Slack */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 group">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-purple-400 transition-colors">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">Slack</h3>
              <p className="text-white/80 text-xs sm:text-sm">
                ¿Te imaginas integrar cualquier software interno en Slack para tus empleados?
              </p>
            </div>

            {/* Email */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 group">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-blue-400 transition-colors">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">Email</h3>
              <p className="text-white/80 text-xs sm:text-sm">
                Recibe reportes, manda emails para vender tus servicios, autorresponde emails por ti...
              </p>
            </div>

            {/* SMS */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 group">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-orange-400 transition-colors">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">Todo lo que te imaginas</h3>
              <p className="text-white/80 text-xs sm:text-sm">
                Genera imágenes, modifica imágenes, genera audio... Todo lo que hace la IA hoy en día se puede integrar y automatizar en tu empresa
              </p>
            </div>
          </div>

          {/* CTA adicional */}
          <div className="text-center mt-12 sm:mt-16">
            <div className="inline-flex items-center px-4 sm:px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
              <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"></div>
              <span className="text-white font-semibold text-sm sm:text-base">
                Todas estas integraciones funcionan automáticamente
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Demo; 